/**
 * Table of contents — built from the H2s in an article's content.
 *
 * The markup ships as an empty <nav data-toc hidden>; this fills it in and
 * unhides it. Doing it in JS rather than Antlers means the list always matches
 * the rendered headings, whatever an editor writes in Bard — there is no second
 * place to keep in sync.
 *
 * Headings get an id when they lack one, so the links have something to target.
 * Ids are slugified from the heading text and de-duplicated, because two
 * sections can legitimately share a title.
 *
 * A scroll-spy highlights the heading currently in view, matching the accordion
 * quick-nav. Smooth scrolling is NOT bound here: the links are real #anchors and
 * initLenis() in site.js already handles every `a[href^="#"]` on the page.
 *
 * No-ops when no [data-toc] is present, which is every non-pillar article.
 */

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function initToc() {
  document.querySelectorAll("[data-toc]").forEach((toc) => {
    if (toc.hasAttribute("data-toc-initialized")) return;
    toc.setAttribute("data-toc-initialized", "");

    const list = toc.querySelector("[data-toc-list]");
    if (!list) return;

    // The content is a sibling of the rail, not a descendant, so this searches
    // the whole article rather than the nav's own subtree.
    const article = toc.closest(".news") || document;
    const content = article.querySelector("[data-toc-content]");
    if (!content) return;

    const headings = Array.from(content.querySelectorAll("h2"));
    // No H2s — leave the nav hidden rather than showing an empty box.
    if (!headings.length) return;

    const usedIds = new Set();

    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      if (!text) return;

      if (!heading.id) {
        let base = slugify(text) || "section";
        let id = base;
        let n = 2;
        // Two sections can share a title; ids cannot.
        while (usedIds.has(id) || document.getElementById(id)) {
          id = `${base}-${n}`;
          n += 1;
        }
        heading.id = id;
      }
      usedIds.add(heading.id);

      const item = document.createElement("li");
      item.className = "news__toc__item";

      const link = document.createElement("a");
      link.className = "news__toc__link";
      link.href = `#${heading.id}`;
      link.textContent = text;
      // Lets the scroll-spy find the matching item without re-querying.
      link.dataset.tocLink = heading.id;

      item.appendChild(link);
      list.appendChild(item);
    });

    // Every heading was blank — nothing was added, so keep it hidden.
    if (!list.children.length) return;

    toc.hidden = false;

    // Offsets the fixed header so a jumped-to heading is not hidden beneath it.
    // Set here rather than in CSS so it applies only to headings the TOC owns.
    headings.forEach((heading) => {
      heading.style.scrollMarginTop = "12rem";
    });

    // --- Scroll-spy ---------------------------------------------------
    const setActive = (id) => {
      list.querySelectorAll("[data-toc-link]").forEach((link) => {
        const item = link.closest(".news__toc__item");
        if (!item) return;
        item.classList.toggle(
          "news__toc__item--active",
          link.dataset.tocLink === id,
        );
      });
    };

    // rootMargin pulls the detection band into the upper part of the viewport,
    // so the active item is the section being read rather than whichever
    // heading happens to touch the bottom edge.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );

    headings.forEach((heading) => observer.observe(heading));
  });
}

export { initToc };
